import React, { useCallback, useEffect, useRef, useState } from "react";
import { Search, History, X } from "lucide-react";
import PeopleSearchSearchBar from "./PeopleSearchSearchBar.tsx";
import styles from "./PeopleSearchSearchBarWithDropdown.module.scss";

export type SearchSuggestion = {
  id: number | string;
  displayName: string;
};

type PeopleSearchSearchBarWithDropdownProps = {
  value: string;
  onChange: (value: string) => void;
  onSearchCommit: (term: string) => void;
  suggestions: SearchSuggestion[];
  suggestionsLoading: boolean;
  history: string[];
  onAddToHistory: (term: string) => void;
  onClearHistory?: () => void;
  placeholder?: string;
  size?: "default" | "large";
};

const SUGGESTIONS_DEBOUNCE_MS = 250;
const MIN_CHARS_FOR_SUGGESTIONS = 3;

const PeopleSearchSearchBarWithDropdown: React.FC<PeopleSearchSearchBarWithDropdownProps> = ({
  value,
  onChange,
  onSearchCommit,
  suggestions,
  suggestionsLoading,
  history,
  onAddToHistory,
  onClearHistory,
  placeholder = "Buscar por nombre o empresa…",
  size = "default",
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const handleCommit = useCallback(
    (term: string) => {
      const trimmed = term.trim();
      if (trimmed) {
        onChange(trimmed);
        onAddToHistory(trimmed);
        onSearchCommit(trimmed);
      }
      setIsOpen(false);
    },
    [onChange, onAddToHistory, onSearchCommit]
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter") {
        e.preventDefault();
        handleCommit(value);
      }
      if (e.key === "Escape") {
        setIsOpen(false);
      }
    },
    [value, handleCommit]
  );

  useEffect(() => {
    const handleClickOutside = (ev: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(ev.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleClearHistory = useCallback(() => {
    onClearHistory?.();
  }, [onClearHistory]);
  const showHistory = isOpen && value.trim().length < MIN_CHARS_FOR_SUGGESTIONS && history.length > 0;
  const showSuggestions = isOpen && value.trim().length >= MIN_CHARS_FOR_SUGGESTIONS;
  const hasContent = showHistory || (showSuggestions && (suggestions.length > 0 || suggestionsLoading));

  return (
    <div
      ref={containerRef}
      className={`${styles.container} ${isOpen ? styles.container_open : ""}`}
    >
      <div
        className={styles.inputWrapper}
        onFocus={() => setIsOpen(true)}
      >
        <PeopleSearchSearchBar
          value={value}
          onChange={onChange}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          size={size}
        />
      </div>
      {hasContent && (
        <div className={styles.dropdown}>
          {showHistory && (
            <div className={styles.section}>
              <div className={styles.sectionHeader}>
                <History size={14} className={styles.sectionIcon} />
                <span className={styles.sectionTitle}>Recientes</span>
                {onClearHistory && history.length > 0 && (
                  <button
                    type="button"
                    className={styles.clearHistory}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleClearHistory();
                    }}
                  >
                    Limpiar
                  </button>
                )}
              </div>
              <ul className={styles.list}>
                {history.slice(0, 8).map((term, idx) => (
                  <li key={`${term}-${idx}`}>
                    <button
                      type="button"
                      className={styles.item}
                      onClick={() => {
                        onChange(term);
                        handleCommit(term);
                      }}
                    >
                      {term}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {showSuggestions && (
            <div className={styles.section}>
              <div className={styles.sectionHeader}>
                <Search size={14} className={styles.sectionIcon} />
                <span className={styles.sectionTitle}>Sugerencias</span>
              </div>
              {suggestionsLoading ? (
                <div className={styles.loading}>
                  <span className={styles.loadingSpinner} />
                  Buscando…
                </div>
              ) : suggestions.length > 0 ? (
                <ul className={styles.list}>
                  {suggestions.map((s) => (
                    <li key={s.id}>
                      <button
                        type="button"
                        className={styles.item}
                        onClick={() => handleCommit(s.displayName)}
                      >
                        {s.displayName}
                      </button>
                    </li>
                  ))}
                </ul>
              ) : (
                <div className={styles.empty}>No hay coincidencias</div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default PeopleSearchSearchBarWithDropdown;
